import { type JSX, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  AudioLines,
  BookOpen,
  ChevronRight,
  Globe,
  Library,
  Orbit,
  Sparkles,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { M365Icon } from "@/features/ai-explorer/components/m365-icon"
import { cn } from "@/lib/utils"

type ToolToggleItemProps = {
  icon: JSX.Element
  label: string
  enabled: boolean
  onToggle?: (enabled: boolean) => void
  count?: number
  hasSubmenu?: boolean
  badge?: string
}

function ToolToggleItem({
  icon,
  label,
  enabled,
  onToggle,
  count,
  hasSubmenu,
  badge,
}: ToolToggleItemProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onToggle?.(!enabled)
    }
  }

  return (
    <div
      aria-checked={enabled}
      role="menuitemcheckbox"
      tabIndex={0}
      className={cn(
        "hover:bg-accent hover:text-accent-foreground my-0.5 flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
        enabled && "text-accent-foreground"
      )}
      onClick={() => onToggle?.(!enabled)}
      onKeyDown={handleKeyDown}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge && (
        <Badge className="h-4 px-1.5 text-[10px] font-medium" variant="secondary">
          {badge}
        </Badge>
      )}
      <span className="ml-auto flex items-center gap-2">
        {typeof count === "number" && count > 0 && (
          <span className="text-muted-foreground text-xs tabular-nums">({count})</span>
        )}
        {hasSubmenu ? (
          <ChevronRight className="text-muted-foreground size-4" />
        ) : (
          <span className="flex size-4 items-center justify-center">
            <span
              className={cn(
                "size-2 rounded-full",
                enabled ? "bg-primary" : "border-muted-foreground/50 border"
              )}
            />
          </span>
        )}
      </span>
    </div>
  )
}

function MenuLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-muted-foreground px-2 py-1.5 text-xs font-normal">{children}</div>
}

export function DemoAgentTools() {
  const { t } = useTranslation()
  const [enableDeep, setEnableDeep] = useState(false)
  const [enableHighReasoning, setEnableHighReasoning] = useState(false)
  const [enableWebSearch, setEnableWebSearch] = useState(true)
  const [enableM365, setEnableM365] = useState(false)

  const iconClassName = (enabled: boolean) =>
    cn("size-4", enabled ? "text-primary" : "text-muted-foreground")

  return (
    <div className="bg-popover text-popover-foreground w-[240px] rounded-md border p-1 shadow-md">
      {/* AI MODES SECTION */}
      <MenuLabel>{t("questioninput.modes_section", { defaultValue: "AI Mode" })}</MenuLabel>
      <ToolToggleItem
        badge={t("deepExplorer.beta_badge", { defaultValue: "Beta" })}
        enabled={enableDeep}
        icon={<Sparkles className={iconClassName(enableDeep)} />}
        label={t("questioninput.deep_mode", { defaultValue: "Deep" })}
        onToggle={setEnableDeep}
      />
      <ToolToggleItem
        enabled={enableHighReasoning}
        icon={<Orbit className={iconClassName(enableHighReasoning)} />}
        label={t("questioninput.high_reasoning", { defaultValue: "High Reasoning" })}
        onToggle={setEnableHighReasoning}
      />

      <Separator className="my-1" />

      {/* KNOWLEDGE SOURCES SECTION */}
      <MenuLabel>
        {t("questioninput.sources_section", { defaultValue: "Knowledge Sources" })}
      </MenuLabel>
      <ToolToggleItem
        hasSubmenu
        count={2}
        enabled={true}
        icon={<Library className={cn("size-4", "text-primary")} />}
        label={t("pages.common.libraries", { defaultValue: "Libraries" })}
      />
      <ToolToggleItem
        enabled={enableWebSearch}
        icon={<Globe className={iconClassName(enableWebSearch)} />}
        label={t("questioninput.web_search", { defaultValue: "Web search" })}
        onToggle={setEnableWebSearch}
      />
      <ToolToggleItem
        enabled={enableM365}
        icon={<M365Icon noText className={iconClassName(enableM365)} />}
        label={t("questioninput.m365_search", { defaultValue: "Microsoft 365" })}
        onToggle={setEnableM365}
      />

      <Separator className="my-1" />

      {/* UTILITIES SECTION */}
      <MenuLabel>{t("questioninput.utilities_section", { defaultValue: "Utilities" })}</MenuLabel>
      <ToolToggleItem
        hasSubmenu
        enabled={false}
        icon={<BookOpen className="text-muted-foreground size-4" />}
        label={t("prompts.title", { defaultValue: "Prompt templates" })}
      />
      <div className="hover:bg-accent hover:text-accent-foreground my-0.5 flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm">
        <AudioLines className="text-muted-foreground size-4" />
        <span className="min-w-0 flex-1 truncate">
          {t("questioninput.start_voice_mode", { defaultValue: "Voice mode" })}
        </span>
        <span className="ml-auto size-4" />
      </div>
    </div>
  )
}

// Keep as alias for backwards compatibility
export const DemoAgentToolsBadges = DemoAgentTools
